/**
 * The shell every authenticated screen sits inside.
 *
 * `screens.md`'s conventions: 1440px app shell, `flex: 0 0 184px` sidebar,
 * `--pp-surface-sunken` sidebar fill, and `data-screen-label` on every screen-level
 * element. The geometry is in `shell.css`; this file is structure and copy.
 *
 * ## Nav items with no destination are text, not links — and never absent
 *
 * Analytics is Phase 7's. A link that navigates to an empty page is a worse answer than a
 * label saying the work has not happened, and **omitting the row is worse than either**:
 * `components.md` — *"The sidebar is the product's map. A nav that grows items as they
 * ship makes the product feel unfinished on every visit instead of once."*
 *
 * **How they are drawn is a function of how many there are** — `pendingTreatment()`,
 * ruled 2026-08-11 (Q5). This file applied the mock's ghost treatment to all four, which
 * put four of six rows at 2.07:1; design overruled that, and the rule reverses itself
 * cleanly as destinations ship.
 *
 * **Products moved out of that list in c34**, when the screen behind it started existing.
 * Moving a row across is the whole mechanism: the day a label becomes a link is the day the
 * work landed, and a row left in `PENDING` after its screen ships is a lie that nothing
 * else in the repo can catch.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { RefObject } from "react";
import { Link } from "@tanstack/react-router";

import { Button } from "@packagepage/ui";

import { pendingTreatment } from "./pending.js";
import { PendingRow } from "./PendingRow.js";
import { useIsFolded } from "./useIsFolded.js";

import "@packagepage/ui/tokens.css";
import "@packagepage/ui/surface.css";
import "@packagepage/ui/button.css";
import "@packagepage/ui/pill.css";
import "./shell.css";

/** A sidebar destination that Block D actually builds. */
interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly count?: number;
}

/** One that it does not, with the commit that will. */
interface PendingNavItem {
  readonly label: string;
}

export interface AppShellProps {
  /** The organization's name — the header's title, per the mock. */
  readonly orgName: string;
  /** Today, spelled the way a person reads it. */
  readonly subtitle: string;
  readonly userName: string;
  /**
   * The account this session belongs to — the menu's header, per Q6.
   *
   * `| undefined` is explicit because this package runs `exactOptionalPropertyTypes`:
   * "may be omitted" and "may be present and undefined" are different types here, and the
   * session's email is the second while it resolves.
   */
  readonly userEmail?: string | undefined;
  readonly role: string;
  readonly productCount?: number;
  readonly headerActions?: ReactNode;
  /**
   * Ends the session.
   *
   * A prop rather than an import, so the shell stays presentational: signing out has to
   * clear the org header and navigate, and a shell that reached for the auth client
   * itself would be a second place that knows how a session ends.
   */
  readonly onSignOut?: () => void;
  readonly children: ReactNode;
}

const PENDING: readonly PendingNavItem[] = [{ label: "Analytics" }];

const LIVE: readonly NavItem[] = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/imports", label: "Import" },
  // Moved out of PENDING at c144/C4, when the screen behind it started existing — which is
  // this list's whole mechanism. Nothing in the repo asserts the pair, and that absence is
  // filed: a PENDING row and a live route for one surface cannot both be true.
  { to: "/domains", label: "Domains" },
  // `/settings` rather than a section, so the row stays lit on every section — U57.
  { to: "/settings", label: "Settings" },
];

/**
 * The identity block and the menu it opens — `components.md` → Shell → Identity, Q6.
 *
 * **Sign out lives here, and takes no red.** The reasoning is a product promise rather than
 * a palette rule: *"Nothing is lost by signing out: every draft, wizard step, mapping
 * template and job is server-persisted, which the product already says out loud."*
 *
 * Two rows the handoff lists are **not built**: `Personal settings` and
 * `Keyboard shortcuts`. Neither destination exists, the handoff itself offers to drop the
 * second (*"drop this row if you would rather not"*), and this repo's standing idiom
 * refuses a door that opens onto nothing — the same rule that keeps the unbuilt nav rows
 * inert rather than linked. They arrive with their screens.
 *
 * ## Escape and outside-click are the contract, not decoration
 *
 * A menu closable only by choosing something is a trap for a keyboard user, and focus has
 * to return to the control that opened it — otherwise dismissing drops the caret at the
 * top of the document. Both are behaviours **no static render can observe**, so they are
 * asserted in `e2e/`, not here.
 */
function AccountMenu({
  userName,
  userEmail,
  role,
  onSignOut,
}: {
  userName: string;
  userEmail?: string | undefined;
  role: string;
  onSignOut?: (() => void) | undefined;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setOpen(false);
      // Back to the control that opened it. Without this the caret lands at the top of the
      // document and a keyboard user walks the whole sidebar again.
      trigger.current?.focus();
    }

    function onPointerDown(event: MouseEvent) {
      if (wrapper.current?.contains(event.target as Node) === true) return;
      setOpen(false);
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  return (
    <div ref={wrapper}>
      {!open || onSignOut === undefined ? null : (
        <div className="pp-shell__menu" role="menu" aria-label="Account">
          <div className="pp-shell__menu-head">
            <span className="pp-shell__menu-name">{userName}</span>
            {userEmail === undefined ? null : (
              <span className="pp-shell__menu-email">{userEmail}</span>
            )}
          </div>
          <div className="pp-shell__menu-divider" />
          <button
            type="button"
            role="menuitem"
            className="pp-shell__menu-item"
            onClick={onSignOut}
          >
            Sign out
          </button>
        </div>
      )}

      <button
        type="button"
        ref={trigger}
        className="pp-shell__identity"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <span className="pp-shell__avatar" aria-hidden="true">
          {initials(userName)}
        </span>
        <span className="pp-shell__identity-lines">
          <span className="pp-shell__identity-name">{userName}</span>
          {/* The ROLE, never the plan tier: the sidebar answers "what can I do here", and
              the plan already appears on the usage card beside the numbers that depend on
              it. */}
          <span className="pp-shell__identity-role">{role}</span>
        </span>
        {/* The literal glyph, not a numeric entity. The entity form trips
            check:design-tokens assertion 5, whose hex-literal pattern reads the digits as
            a colour — a false positive worth taking the hint from rather than working
            around, since an entity is a less legible way to write a character. */}
        <span className="pp-shell__caret" aria-hidden="true">
          ▾
        </span>
      </button>
    </div>
  );
}

/** Two letters for the avatar. Initials, never a generated image — no third-party anything. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

/**
 * The drawer's keyboard contract — Esc, focus in, focus back.
 *
 * `components.md`: *"focus trapped, Esc closes, closes on navigate."* A drawer that
 * covers the page while focus stays behind it is worse than no drawer: a keyboard user
 * tabs through content they cannot see, and a screen-reader user is never told the page
 * changed. None of this is observable by a static render, so it is asserted in `e2e/`.
 *
 * The trap is deliberately the small version — wrap Tab at the ends — rather than an
 * inert-everything-else implementation. `inert` on the main column would be stronger and
 * is a larger change to the shell's structure; this is honest about being the smaller one.
 */
function useDrawerBehaviour(
  open: boolean,
  close: () => void,
  drawer: RefObject<HTMLElement | null>,
  trigger: RefObject<HTMLButtonElement | null>,
): void {
  useEffect(() => {
    if (!open) return;

    const node = drawer.current;
    node?.querySelector<HTMLElement>("a, button")?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        close();
        trigger.current?.focus();
        return;
      }
      if (event.key !== "Tab" || node === null) return;

      const focusable = [...node.querySelectorAll<HTMLElement>("a[href], button")].filter(
        (el) => !el.hasAttribute("disabled"),
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close, drawer, trigger]);
}

export function AppShell({
  orgName,
  subtitle,
  userName,
  userEmail,
  role,
  productCount,
  headerActions,
  onSignOut,
  children,
}: AppShellProps): ReactNode {
  // Derived from the list, so the day a destination ships the treatment changes with it.
  // One row is left since U78 moved Import out, which is the ghost branch.
  const treatment = pendingTreatment(PENDING.length);

  const folded = useIsFolded();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  useDrawerBehaviour(drawerOpen && folded, closeDrawer, drawerRef, menuButtonRef);

  // Crossing the fold with the drawer open would leave a desktop sidebar carrying an
  // open-drawer state nothing can close, because the button that closes it is hidden
  // above the fold.
  useEffect(() => {
    if (!folded) setDrawerOpen(false);
  }, [folded]);

  const account = (
    <AccountMenu
      userName={userName}
      userEmail={userEmail}
      role={role}
      onSignOut={onSignOut}
    />
  );

  return (
    <div className="pp-shell" data-screen-label="App shell">
      <nav
        className="pp-shell__sidebar"
        id="pp-shell-drawer"
        aria-label="Sections"
        ref={drawerRef}
        data-open={folded && drawerOpen ? "true" : undefined}
        /**
         * `inert`, **because `aria-hidden` was the wrong tool and the test proved it.**
         *
         * A transform hides nothing from a screen reader or from Tab, so the closed
         * drawer needs to be taken out of both. This shipped as `aria-hidden`, and the
         * fold spec measured what that actually does: the links leave the accessibility
         * tree — `getByRole` stops finding them — and **stay in the tab order**. On a
         * folded shell one Tab from a fresh page still landed on the off-screen Home
         * link. The commit that added it claimed it covered "a screen reader or Tab";
         * it covered the first and not the second.
         *
         * Swapping to `inert` alone then failed the *other* half, which is the part
         * worth keeping: measured, the two attributes have **opposite strengths**.
         *
         *   aria-hidden   removed from the accessibility tree   still tabbable
         *   inert         not tabbable, not clickable           still role-queryable
         *
         * So both are applied, and this is one of the few places where that pairing is
         * correct rather than redundant: `aria-hidden` on focusable content is normally
         * an ARIA violation, and `inert` is what makes the content non-focusable, so the
         * combination is exactly the case the violation does not apply to.
         *
         * One honesty note on the evidence: the second row is what **Playwright's role
         * query** reports, and its query models `aria-hidden` but does not appear to
         * model `inert`. That may understate what a real screen reader does with an inert
         * subtree. It is stated as measured rather than as a claim about assistive tech,
         * and applying both means each property has an assertion that can actually see
         * it — which a single attribute did not.
         *
         * Worth stating plainly because it is the fourth of its kind this week: the
         * *observation* (links off-screen but present) was right, and the *remedy
         * attached to it* was assumed rather than measured — twice, in opposite
         * directions, before the pair was.
         */
        inert={folded && !drawerOpen}
        aria-hidden={folded && !drawerOpen ? true : undefined}
      >
        <div className="pp-shell__wordmark">
          <span className="pp-shell__mark" aria-hidden="true">
            p
          </span>
          <span>packagepage</span>
        </div>

        <div className="pp-shell__nav">
          {LIVE.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              activeProps={{ "data-active": "true" }}
              // Exact only for `/`, which is a prefix of every other route. Products has
              // to stay lit while a product detail is open — the row names the section,
              // not the URL.
              activeOptions={{ exact: item.to === "/" }}
              // "Closes on navigate" — a drawer left open over the screen the user just
              // asked for makes the destination invisible at the moment it arrives.
              onClick={closeDrawer}
            >
              <span>{item.label}</span>
              {item.to === "/products" && productCount !== undefined ? (
                <span className="pp-shell__count">{productCount}</span>
              ) : item.count === undefined ? null : (
                <span className="pp-shell__count">{item.count}</span>
              )}
            </Link>
          ))}
          {PENDING.map((item) => (
            <PendingRow key={item.label} label={item.label} treatment={treatment} />
          ))}
        </div>

        {folded ? null : <div className="pp-shell__footer">{account}</div>}
      </nav>

      {!folded ? null : (
        <div className="pp-shell__topbar">
          <button
            type="button"
            ref={menuButtonRef}
            className="pp-shell__menu-button"
            aria-label="Sections"
            aria-expanded={drawerOpen}
            aria-controls="pp-shell-drawer"
            onClick={() => setDrawerOpen((wasOpen) => !wasOpen)}
          >
            {/* Three rules, drawn rather than iconified: there is no icon set in this
                system, which is the same reason the ruling forbids an icon rail. */}
            <span aria-hidden="true">☰</span>
          </button>
          <div className="pp-shell__wordmark">
            <span className="pp-shell__mark" aria-hidden="true">
              p
            </span>
            <span>packagepage</span>
          </div>
          <div className="pp-shell__footer">{account}</div>
        </div>
      )}

      {!folded || !drawerOpen ? null : (
        <button
          type="button"
          className="pp-shell__scrim"
          aria-label="Close sections"
          onClick={closeDrawer}
        />
      )}

      <div className="pp-shell__main">
        <header className="pp-shell__header">
          <div>
            <div className="pp-shell__title">{orgName}</div>
            <div className="pp-shell__subtitle">{subtitle}</div>
          </div>
          <div className="pp-shell__actions">{headerActions}</div>
        </header>
        <main className="pp-shell__content">{children}</main>
      </div>
    </div>
  );
}

/**
 * The three header actions, with the third built **as ruled**.
 *
 * *"Publish a recall notice"* is `screens.md` §2's required amendment and open-decision
 * #1 is QUEUED — so what ships is the ruling, not the mock. **Permanently neutral**: a
 * `secondary` variant, never `destructive`, never `--pp-recall`, and never celebratory.
 * The reasoning is the invariant behind it: colour on this screen is reserved for a
 * recall that is *live*, and spending it on the button that starts one would mean the
 * dashboard looks alarming to a brand that has never had an incident.
 *
 * "Preview a scan" still says plainly that it is not built, rather than navigating somewhere
 * blank. **"Add a product" and "Publish a recall notice" are links** to built screens: a
 * `Link` wearing the button's own classes rather than a `Button` with an `onClick` that
 * navigates, so it carries an href a user can middle-click, copy and read in the status
 * bar — the same reasoning that keeps the table's row-open a link.
 */
export function DashboardActions({
  onPending,
}: {
  onPending: (what: string) => void;
}): ReactNode {
  return (
    <>
      <Button variant="secondary" onClick={() => onPending("Preview a scan")}>
        Preview a scan
      </Button>
      <Link to="/recalls/new" className="pp-button" data-variant="secondary">
        Publish a recall notice
      </Link>
      <Link to="/products/new" className="pp-button" data-variant="primary">
        Add a product
      </Link>
    </>
  );
}
